/**
 * Which full orders fetch may write the list — pure decision, one guard per App instance.
 *
 * Why this exists: several full fetches of the orders list overlap (the boot fetch, the visibility
 * resync, polling, refreshOrders, the event ledger's coalesced resync, the rider check-in refresh).
 * Each one reads a snapshot on the server, then assigns it to the list when it resolves. Two things
 * go wrong with an unconditional assignment:
 *   1. Two fetches resolve out of order: the one that STARTED earlier (an older snapshot) resolves
 *      last and overwrites the newer list.
 *   2. A realtime event is applied to the list while a fetch is in flight: the fetch then assigns a
 *      snapshot that may predate the event, and the event's change disappears from the tablet.
 *
 * Usage: `begin()` before the request, `settle(token)` after the response is processed and before
 * anything is toasted or assigned. A query that started later and was already applied has a newer
 * snapshot, so the older one is dropped. A query that overlapped a realtime event is applied (its
 * snapshot still holds the rest of the list) and the caller fetches once more, so the event cannot
 * be lost.
 *
 * App.vue owns the side effects (fetch, toasts, assignment, the follow-up fetch). No timers, no I/O.
 */

/**
 * @returns {{
 *   begin: () => {gen: number, eventGen: number},
 *   noteEvent: () => void,
 *   settle: (token: {gen: number, eventGen: number}) => 'drop'|'apply'|'apply+refetch',
 * }}
 */
export function createOrdersQueryGuard() {
  let started = 0;      // generation handed to the last query that began
  let lastApplied = 0;  // highest generation whose snapshot was applied
  let eventGen = 0;     // bumped every time a realtime event changed the list

  return {
    /** A full fetch is about to start. */
    begin() {
      started += 1;
      return { gen: started, eventGen };
    },
    /** A realtime event changed the order list. */
    noteEvent() {
      eventGen += 1;
    },
    /**
     * The fetch has its answer.
     * 'drop': a query that started later was already applied, this snapshot is older.
     * 'apply': write it.
     * 'apply+refetch': write it, then fetch once more: an event landed while it was in flight.
     */
    settle(token) {
      if (token.gen < lastApplied) return 'drop';
      lastApplied = token.gen;
      return eventGen !== token.eventGen ? 'apply+refetch' : 'apply';
    },
  };
}
